import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, unwrap } from '../api';
import { imageUrl, imageSrcSet } from '../api/image';
import { resolvePath } from '../api/resolve';
import analytics from '../services/analytics';
import './FighterDetailPage.css';

const DISCIPLINES = { bare_knuckle: 'Bare knuckle', mma: 'MMA', boxing: 'Boxing' };
const capitalize = s => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '');
const careerLabel = r =>
  [DISCIPLINES[r.discipline] || capitalize(r.discipline), capitalize(r.level)]
    .filter(Boolean)
    .join(' · ');
const recordLine = r => `${r.wins}-${r.losses}-${r.draws}`;

// Webflow bios are HTML. Render the text only; never inject the markup.
const bioText = html =>
  new DOMParser().parseFromString(html || '', 'text/html').body.textContent.trim();

const watchHref = w => `/watch/${w.slug || w.media_id}`;

/** "Win · TKO · R2"; an undetermined bout is pending, never a draw. */
const resultLabel = bout => {
  if (bout.result_state === 'undetermined') return 'Result pending';
  if (bout.result_state !== 'determined') return capitalize(bout.result_state);
  return [capitalize(bout.outcome), bout.method, bout.end_round ? `R${bout.end_round}` : null]
    .filter(Boolean)
    .join(' · ');
};

const BackLink = ({ className = '' }) => (
  <Link to="/fighters" className={`fighter-page__back ${className}`}>
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
    Back to Fighters
  </Link>
);

const StatLine = ({ label, line }) =>
  line ? (
    <tr>
      <th scope="row">{label}</th>
      <td>
        {line.red ? `${line.red.strike_landed}/${line.red.strike_attempt}` : '–'}
        {line.red?.strike_accuracy_pct != null && ` (${line.red.strike_accuracy_pct}%)`}
        {line.red ? `, KD ${line.red.knockdown}` : ''}
      </td>
      <td>
        {line.blue ? `${line.blue.strike_landed}/${line.blue.strike_attempt}` : '–'}
        {line.blue?.strike_accuracy_pct != null && ` (${line.blue.strike_accuracy_pct}%)`}
        {line.blue ? `, KD ${line.blue.knockdown}` : ''}
      </td>
    </tr>
  ) : null;

/** Combatreg strike stats for one bout, fetched when the row is first expanded. */
const BoutStats = ({ boutId }) => {
  const [stats, setStats] = useState(undefined);
  useEffect(() => {
    unwrap(api.GET('/v1/bouts/{id}/stats', { params: { path: { id: boutId } } }))
      .then(body => setStats(body.data))
      .catch(error => {
        console.error('Error fetching bout stats:', error);
        setStats(null);
      });
  }, [boutId]);

  if (stats === undefined) return <p className="fighter-bout__stats-note">Loading stats…</p>;
  if (!stats?.totals) return <p className="fighter-bout__stats-note">No stats for this bout</p>;
  const name = corner => stats.participants?.find(p => p.corner === corner)?.name || corner;
  return (
    <table className="fighter-bout__stats" data-testid="bout-stats">
      <thead>
        <tr>
          <th scope="col">Strikes landed</th>
          <th scope="col">{name('red')}</th>
          <th scope="col">{name('blue')}</th>
        </tr>
      </thead>
      <tbody>
        <StatLine label="Total" line={stats.totals} />
        {(stats.rounds || []).map(r => (
          <StatLine key={r.round_number} label={`Round ${r.round_number}`} line={r} />
        ))}
      </tbody>
    </table>
  );
};

const BoutRow = ({ bout }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="fighter-bout" data-testid="fighter-bout">
      <div className="fighter-bout__main">
        <div>
          <Link to={`/events/${bout.event.slug || bout.event.id}`} className="fighter-bout__event">
            {bout.event.name}
          </Link>
          <span className="fighter-bout__date">{bout.event.event_date}</span>
          <div className="fighter-bout__opponent">
            vs{' '}
            {bout.opponent?.slug ? (
              <Link to={`/fighters/${bout.opponent.slug}`}>{bout.opponent.name}</Link>
            ) : (
              bout.opponent?.name || 'TBD'
            )}
          </div>
          <div className="fighter-bout__result">{resultLabel(bout)}</div>
        </div>
        <div className="fighter-bout__actions">
          {(bout.watch || []).map(w => (
            <Link
              key={`${w.media_id}-${w.role}`}
              to={watchHref(w)}
              className="fighter-bout__watch"
              data-testid="bout-watch"
            >
              {w.role === 'contains' ? 'Watch in full card' : 'Watch fight'}
            </Link>
          ))}
          <button
            type="button"
            className="fighter-bout__toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? 'Hide stats' : 'Show stats'}
          </button>
        </div>
      </div>
      {expanded && <BoutStats boutId={bout.bout_id} />}
    </li>
  );
};

const FighterDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fighter, setFighter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    unwrap(api.GET('/v1/fighters/{id}', { params: { path: { id } } }))
      .then(body => {
        if (!active) return;
        // Loaded under a legacy Webflow id: the address bar takes the slug, and the page loads
        // (and is counted) once, under it.
        if (body.data.slug && body.data.slug !== id) {
          navigate(`/fighters/${body.data.slug}`, { replace: true });
          return;
        }
        setLoading(false);
        setFighter(body.data);
        analytics.trackFighterView({
          id: body.data.id,
          name: body.data.name,
          weight_class: body.data.weight_class?.key,
        });
      })
      .catch(async err => {
        if (err?.code !== 'NOT_FOUND') console.error('Error fetching fighter:', err);
        // A merged fighter's old slug is not served by the profile route; /v1/resolve follows it.
        const canonical = err?.code === 'NOT_FOUND' && (await resolvePath(`/fighters/${id}`));
        if (!active) return;
        if (canonical && canonical !== `/fighters/${id}`) {
          navigate(canonical, { replace: true });
          return;
        }
        setLoading(false);
        setError(err?.code === 'NOT_FOUND' ? 'Fighter not found' : 'Failed to load fighter data');
      });
    return () => {
      active = false;
    };
  }, [id, navigate]);

  if (loading) {
    return (
      <div
        className="fighter-page"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className="fighter-content__spinner" />
      </div>
    );
  }

  if (error || !fighter) {
    return (
      <div className="fighter-page">
        <div className="fighter-page__error">
          <h2>Fighter Not Found</h2>
          <p>{error || 'Unable to load fighter information.'}</p>
          <BackLink />
        </div>
      </div>
    );
  }

  const heroBg = imageUrl(fighter.art?.landscape, '16x9', 1280);
  const bodyImage = imageUrl(fighter.body_image, 'fit', 640);
  const countryFlag = imageUrl(fighter.country_flag, 'fit', 160);
  const division = fighter.weight_class?.name;
  const titleLabel = fighter.champion
    ? 'Champion'
    : fighter.interim_champion
      ? 'Interim Champion'
      : null;
  const bkfc = fighter.records?.bkfc;
  const career = fighter.records?.career || [];
  const bouts = fighter.bouts || [];
  const media = fighter.media || [];
  const bio = bioText(fighter.bio);

  return (
    <div className="fighter-page">
      {/* ── Hero Section ── */}
      <div className="fighter-hero" style={heroBg ? { '--hero-bg': `url(${heroBg})` } : undefined}>
        <div className="fighter-hero__watermark" aria-hidden="true">
          Bare Knuckle Fighting
        </div>

        {bodyImage && (
          <img
            src={bodyImage}
            srcSet={imageSrcSet(fighter.body_image, 'fit', 1280)}
            sizes="28vw"
            alt={fighter.name}
            className="fighter-hero__body"
            onError={e => {
              e.target.style.display = 'none';
            }}
          />
        )}

        <div className="fighter-hero__inner">
          <BackLink className="fighter-hero__back" />

          <div className="fighter-hero__content">
            {(titleLabel || fighter.country_code) && (
              <div className="fighter-hero__meta">
                {titleLabel && (
                  <div className="fighter-hero__champion-badge">
                    <svg className="fighter-hero__belt-icon" viewBox="0 0 40 22" fill="#0a0a0a">
                      <path d="M0 8h13v6H0z" />
                      <path d="M27 8h13v6H27z" />
                      <ellipse cx="20" cy="11" rx="10" ry="10.5" />
                      <ellipse cx="20" cy="11" rx="6.2" ry="6.8" fill="#d9b53f" />
                      <path
                        d="M16.6 12.7v-2.8l1.8 1.4 1.6-2.3 1.6 2.3 1.8-1.4v2.8z"
                        fill="#0a0a0a"
                      />
                    </svg>
                    <span className="fighter-hero__champion-text">
                      {division
                        ? `${division} ${titleLabel}`.toUpperCase()
                        : titleLabel.toUpperCase()}
                    </span>
                  </div>
                )}

                {fighter.country_code && (
                  <div className="fighter-hero__country">
                    {countryFlag && (
                      <img src={countryFlag} alt="" className="fighter-hero__country-flag" />
                    )}
                    <span className="fighter-hero__country-text">{fighter.country_code}</span>
                  </div>
                )}
              </div>
            )}

            <h1 className="fighter-hero__name">{fighter.name}</h1>
            {fighter.nick_name && <p className="fighter-hero__nickname">“{fighter.nick_name}”</p>}

            {bkfc && (
              <div data-testid="bkfc-record">
                <p className="fighter-hero__record-label">BKFC record</p>
                <div className="fighter-hero__stats">
                  <div className="fighter-hero__stat-box">
                    <span className="fighter-hero__stat-value">{bkfc.wins}</span>
                    <span className="fighter-hero__stat-label">Wins</span>
                  </div>
                  <span className="fighter-hero__stat-dash">–</span>
                  <div className="fighter-hero__stat-box">
                    <span className="fighter-hero__stat-value">{bkfc.losses}</span>
                    <span className="fighter-hero__stat-label">Losses</span>
                  </div>
                  <span className="fighter-hero__stat-dash">–</span>
                  <div className="fighter-hero__stat-box">
                    <span className="fighter-hero__stat-value">{bkfc.draws}</span>
                    <span className="fighter-hero__stat-label">Draws</span>
                  </div>
                  <span className="fighter-hero__stat-dash" />
                  <div className="fighter-hero__stat-box">
                    <span className="fighter-hero__stat-value">{bkfc.kos}</span>
                    <span className="fighter-hero__stat-label">KOs</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="fighter-titles">
        {career.length > 0 && (
          <section data-testid="career-records">
            <h2 className="fighter-titles__heading">Career record</h2>
            <ul className="fighter-career">
              {career.map(r => (
                <li key={`${r.discipline}-${r.level}`}>
                  <span>{careerLabel(r)}</span> <strong>{recordLine(r)}</strong>
                </li>
              ))}
            </ul>
          </section>
        )}

        {bio && (
          <section>
            <h2 className="fighter-titles__heading">Bio</h2>
            <p className="fighter-bio">{bio}</p>
          </section>
        )}

        {bouts.length > 0 && (
          <section>
            <h2 className="fighter-titles__heading">Fight history</h2>
            <ul className="fighter-bouts">
              {bouts.map(b => (
                <BoutRow key={b.bout_id} bout={b} />
              ))}
            </ul>
          </section>
        )}

        {media.length > 0 && (
          <section>
            <h2 className="fighter-titles__heading">Videos</h2>
            <div className="fighter-media">
              {media.map(m => (
                <Link
                  key={m.id || m.slug}
                  to={`/watch/${m.slug || m.id}`}
                  className="fighter-media__card"
                >
                  <img
                    src={imageUrl(m.art?.landscape, '16x9', 480)}
                    srcSet={imageSrcSet(m.art?.landscape, '16x9', 960)}
                    sizes="320px"
                    alt={m.title?.value || ''}
                    loading="lazy"
                  />
                  <span>{m.title?.value}</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default FighterDetailPage;
